'use client';

import { Gantt } from '@svar-ui/react-gantt';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { uiConfig } from '@web/constants';
import { formatDate } from '@web/utils';

import styles from './GanttChart.module.scss';

import type { Dayjs } from 'dayjs';
import type { CSSProperties } from 'react';

export type GanttScale = 'days' | 'weeks' | 'months' | 'year';

export type GanttTask = {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  durationDays?: number;
  rowMeta?: string;
};

export type GanttLink = {
  id: string;
  sourceTaskId: string;
  targetTaskId: string;
  type?: 'finish_to_start';
};

export type GanttMarker = {
  id: string;
  label: string;
  date: string;
  color: string;
};

export type GanttTaskChange = {
  taskId: string;
  startDate: string;
  endDate: string;
  durationDays: number;
};

export type GanttLinkChange =
  | { type: 'create'; sourceTaskId: string; targetTaskId: string }
  | { type: 'delete'; linkId: string };

export type GanttColumn = {
  id: string;
  header: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  sort?: boolean;
  getter?: (row: any) => unknown;
  cell?: (props: any) => unknown;
};

type GanttChartProps = {
  tasks: GanttTask[];
  links: GanttLink[];
  markers?: GanttMarker[];
  columns?: GanttColumn[];
  scale: GanttScale;
  zoom: number;
  readOnly?: boolean;
  viewportStartDate?: string;
  viewportEndDate?: string;
  className?: string;
  onTaskChange?: (change: GanttTaskChange) => void;
  onLinkChange?: (change: GanttLinkChange) => void;
};

const defaultCellHeight = 70;

type GanttGridMeasurement = {
  scale: GanttScale;
  zoom: number;
  stepPx: number;
};

type GanttChartMeasurement = {
  scale: GanttScale;
  zoom: number;
  leftPx: number;
  scrollLeft: number;
};

function toIsoDate(value: Dayjs | null) {
  if (!value) {
    return null;
  }
  return value.format('YYYY-MM-DD');
}

function clampZoom(value: number) {
  return Math.max(0, Math.min(100, value));
}

function toCellWidth(zoom: number) {
  const z = clampZoom(zoom);
  return 20 + Math.round((z / 100) * 56);
}

function formatWeekRange(date: Date) {
  const start = dayjs(date);
  return start.format('MMM DD, YYYY');
}

function formatMonthYear(date: Date) {
  return dayjs(date).format('MMM YYYY');
}

function toScales(scale: GanttScale) {
  if (scale === 'year') {
    return [{ unit: 'month', step: 1, format: formatMonthYear }];
  }
  if (scale === 'months') {
    return [
      { unit: 'month', step: 1, format: formatMonthYear },
      { unit: 'week', step: 1, format: formatWeekRange },
    ];
  }
  if (scale === 'weeks') {
    return [
      { unit: 'month', step: 1, format: '%F %Y' },
      { unit: 'week', step: 1, format: formatWeekRange },
    ];
  }
  return [
    { unit: 'month', step: 1, format: '%F %Y' },
    { unit: 'day', step: 1, format: '%j' },
  ];
}

function toBottomScaleCellDays(scale: GanttScale) {
  if (scale === 'days') {
    return 1;
  }
  if (scale === 'weeks' || scale === 'months') {
    return 7;
  }
  return 30.4375;
}

function toScaleRangeStart(date: string | null, scale: GanttScale) {
  if (!date) {
    return null;
  }
  const value = dayjs(date).startOf('day');
  if (!value.isValid()) {
    return null;
  }
  if (scale === 'year') {
    return value.startOf('month').format('YYYY-MM-DD');
  }
  if (scale === 'weeks' || scale === 'months') {
    return value.startOf('week').format('YYYY-MM-DD');
  }
  return value.format('YYYY-MM-DD');
}

function normalizeTaskDateRange(startDate: string, endDate: string) {
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');
  if (!end.isAfter(start)) {
    return { start, end: start.add(1, 'day') };
  }
  return { start, end };
}

function markerDayOffset(
  markerDate: string,
  rangeStartDate?: string,
  rangeEndDate?: string,
) {
  if (!rangeStartDate || !rangeEndDate) {
    return null;
  }
  const viewportStart = dayjs(rangeStartDate).startOf('day');
  const viewportEnd = dayjs(rangeEndDate).startOf('day');
  const marker = dayjs(markerDate).startOf('day');
  if (!viewportStart.isValid() || !viewportEnd.isValid() || !marker.isValid()) {
    return null;
  }
  const totalDays = Math.max(viewportEnd.diff(viewportStart, 'day'), 1);
  const offsetDays = marker.diff(viewportStart, 'day');
  return Math.max(0, Math.min(totalDays, offsetDays));
}

export function GanttChart({
  tasks,
  links,
  markers = [],
  columns,
  scale,
  zoom,
  readOnly = false,
  viewportStartDate,
  viewportEndDate,
  className,
  onTaskChange,
  onLinkChange,
}: GanttChartProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [gridMeasurement, setGridMeasurement] =
    useState<GanttGridMeasurement | null>(null);
  const [chartMeasurement, setChartMeasurement] =
    useState<GanttChartMeasurement | null>(null);

  const scales = useMemo(() => toScales(scale), [scale]);
  const cellWidth = useMemo(() => toCellWidth(zoom), [zoom]);
  const activeGridStepPx =
    gridMeasurement?.scale === scale && gridMeasurement.zoom === zoom
      ? gridMeasurement.stepPx
      : null;
  const activeChartMeasurement =
    chartMeasurement?.scale === scale && chartMeasurement.zoom === zoom
      ? chartMeasurement
      : null;
  const dayWidthPx = useMemo(
    () =>
      activeGridStepPx === null
        ? null
        : activeGridStepPx / toBottomScaleCellDays(scale),
    [activeGridStepPx, scale],
  );

  const chartRange = useMemo(() => {
    const taskStarts = tasks
      .map((task) => dayjs(task.startDate).startOf('day'))
      .filter((value) => value.isValid());
    const taskEnds = tasks
      .map((task) => dayjs(task.endDate).startOf('day'))
      .filter((value) => value.isValid());

    const explicitStart =
      viewportStartDate && dayjs(viewportStartDate).isValid()
        ? dayjs(viewportStartDate).startOf('day')
        : null;
    const explicitEnd =
      viewportEndDate && dayjs(viewportEndDate).isValid()
        ? dayjs(viewportEndDate).startOf('day')
        : null;
    const firstTaskStart =
      taskStarts.length > 0
        ? taskStarts.reduce((min, value) => (value.isBefore(min) ? value : min))
        : null;
    const lastTaskEnd =
      taskEnds.length > 0
        ? taskEnds.reduce((max, value) => (value.isAfter(max) ? value : max))
        : null;

    const sourceStart =
      explicitStart && firstTaskStart
        ? explicitStart.isBefore(firstTaskStart)
          ? explicitStart
          : firstTaskStart
        : (explicitStart ?? firstTaskStart);

    const sourceEnd =
      explicitEnd && lastTaskEnd
        ? explicitEnd.isAfter(lastTaskEnd)
          ? explicitEnd
          : lastTaskEnd
        : (explicitEnd ?? lastTaskEnd);

    if (!sourceStart || !sourceEnd) {
      return { start: null as string | null, end: null as string | null };
    }

    // Keep a small buffer so Start/Finish markers aren't pinned to chart edges.
    return {
      start: sourceStart.subtract(2, 'day').format('YYYY-MM-DD'),
      end: sourceEnd.add(2, 'day').format('YYYY-MM-DD'),
    };
  }, [tasks, viewportEndDate, viewportStartDate]);

  const markerRangeStart = useMemo(
    () => toScaleRangeStart(chartRange.start, scale),
    [chartRange.start, scale],
  );

  const svarTasks = useMemo(
    () =>
      tasks.map((task) => {
        const normalized = normalizeTaskDateRange(task.startDate, task.endDate);
        return {
          id: task.id,
          text: task.label,
          start: normalized.start.toDate(),
          end: normalized.end.toDate(),
          details: task.rowMeta ?? '',
        };
      }),
    [tasks],
  );

  const svarLinks = useMemo(
    () =>
      links.map((link) => ({
        id: link.id,
        source: link.sourceTaskId,
        target: link.targetTaskId,
        type: link.type === 'finish_to_start' ? 'e2s' : 'e2s',
      })),
    [links],
  );

  const visualMarkers = useMemo(
    () =>
      markers
        .map((marker) => ({
          ...marker,
          dayOffset: markerDayOffset(
            marker.date,
            markerRangeStart ?? undefined,
            chartRange.end ?? undefined,
          ),
        }))
        .filter((marker) => marker.dayOffset !== null),
    [chartRange.end, markerRangeStart, markers],
  );

  const handleInit = useCallback(
    (api: any) => {
      api.on?.('update-task', (event: any) => {
        const task = event?.task;
        const taskId = String(task?.id ?? '');
        if (!taskId) {
          return;
        }
        const start = dayjs(task?.start);
        const endExclusive = dayjs(task?.end);
        const startDate = toIsoDate(start);
        if (!startDate || !endExclusive.isValid()) {
          return;
        }
        const durationDays = Math.max(endExclusive.diff(start, 'day'), 1);
        const endDate = endExclusive.format('YYYY-MM-DD');
        onTaskChange?.({
          taskId,
          startDate,
          endDate,
          durationDays,
        });
      });

      api.on?.('add-link', (event: any) => {
        const sourceTaskId = String(event?.source ?? '');
        const targetTaskId = String(event?.target ?? '');
        if (!sourceTaskId || !targetTaskId) {
          return;
        }
        onLinkChange?.({
          type: 'create',
          sourceTaskId,
          targetTaskId,
        });
      });

      api.on?.('delete-link', (event: any) => {
        const linkId = String(event?.id ?? '');
        if (!linkId) {
          return;
        }
        onLinkChange?.({
          type: 'delete',
          linkId,
        });
      });
    },
    [onLinkChange, onTaskChange],
  );
  const defaultColumns = useMemo<GanttColumn[]>(
    () => [
      { id: 'text', header: 'Deliverables', width: 150, sort: false },
      {
        id: 'start',
        header: 'Start date',
        width: 120,
        align: 'center' as const,
        sort: false,
        getter: (row: any) => formatDate(row?.start, uiConfig.dateFormats.date),
      },
      {
        id: 'duration',
        header: 'Duration',
        width: 90,
        align: 'center' as const,
        sort: false,
      },
      {
        id: 'due',
        header: 'Due date',
        width: 120,
        align: 'center' as const,
        sort: false,
        getter: (row: any) => formatDate(row?.end, uiConfig.dateFormats.date),
      },
    ],
    [],
  );
  const resolvedColumns = columns ?? defaultColumns;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let animationFrameId: number | null = null;

    const readBottomScaleCellWidth = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        const cell = wrapper.querySelector(
          '.wx-scale .wx-row:last-child .wx-cell',
        ) as HTMLElement | null;
        if (!cell) return;
        const width = Math.round(cell.getBoundingClientRect().width);
        if (width > 0) {
          setGridMeasurement((current) =>
            current?.scale === scale &&
            current.zoom === zoom &&
            current.stepPx === width
              ? current
              : { scale, zoom, stepPx: width },
          );
        }
      });
    };

    readBottomScaleCellWidth();

    const resizeObserver = new ResizeObserver(() => {
      readBottomScaleCellWidth();
    });
    resizeObserver.observe(wrapper);

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      resizeObserver.disconnect();
    };
  }, [scale, zoom, tasks.length]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const chart = wrapper.querySelector('.wx-chart') as HTMLElement | null;
    if (!chart) return;

    let animationFrameId: number | null = null;

    const updateChartMetrics = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        const wrapperRect = wrapper.getBoundingClientRect();
        const chartRect = chart.getBoundingClientRect();
        const nextMeasurement = {
          scale,
          zoom,
          leftPx: Math.max(chartRect.left - wrapperRect.left, 0),
          scrollLeft: chart.scrollLeft,
        };
        setChartMeasurement((current) =>
          current?.scale === nextMeasurement.scale &&
          current.zoom === nextMeasurement.zoom &&
          current.leftPx === nextMeasurement.leftPx &&
          current.scrollLeft === nextMeasurement.scrollLeft
            ? current
            : nextMeasurement,
        );
      });
    };

    updateChartMetrics();
    chart.addEventListener('scroll', updateChartMetrics, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      updateChartMetrics();
    });
    resizeObserver.observe(wrapper);
    resizeObserver.observe(chart);

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      chart.removeEventListener('scroll', updateChartMetrics);
      resizeObserver.disconnect();
    };
  }, [scale, zoom, tasks.length]);

  return (
    <div
      ref={wrapperRef}
      className={clsx(styles.ganttChart, className)}
      style={
        {
          '--nn-gantt-cell-width': `${cellWidth}px`,
          '--nn-gantt-grid-step':
            activeGridStepPx !== null
              ? `${activeGridStepPx}px`
              : `${cellWidth}px`,
        } as CSSProperties
      }
    >
      <div className={styles.ganttChart__surface}>
        <Gantt
          init={handleInit}
          tasks={svarTasks as any}
          links={svarLinks as any}
          columns={resolvedColumns as any}
          scales={scales as any}
          lengthUnit="day"
          durationUnit="day"
          schedule={{ auto: false }}
          cellBorders="column"
          cellWidth={cellWidth}
          cellHeight={defaultCellHeight}
          readonly={readOnly}
          start={
            chartRange.start ? dayjs(chartRange.start).toDate() : undefined
          }
          end={chartRange.end ? dayjs(chartRange.end).toDate() : undefined}
        />

        {visualMarkers.length > 0 &&
        dayWidthPx !== null &&
        activeChartMeasurement ? (
          <div className={styles.ganttChart__markers} aria-hidden>
            {visualMarkers.map((marker) => (
              <div
                key={marker.id}
                className={styles.ganttChart__marker}
                style={
                  {
                    left: `${(
                      activeChartMeasurement.leftPx +
                      (Number(marker.dayOffset ?? 0) + 0.5) * dayWidthPx -
                      activeChartMeasurement.scrollLeft
                    ).toFixed(2)}px`,
                    backgroundColor: marker.color,
                    borderColor: marker.color,
                    '--nn-gantt-marker-color': marker.color,
                  } as CSSProperties
                }
              >
                <span className={styles.ganttChart__markerLabel}>
                  {marker.label}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
